import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { talentAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Calendar, Clock, DollarSign, Users,
  AlertTriangle, CheckCircle, Loader2, Building2, ShirtIcon,
  ListChecks, BadgeCheck, Sparkles,
} from 'lucide-react';
import { formatDateFull, formatTime12h, formatLongDate, formatTimeRange, tzAbbrev, formatEventWhen } from '../../utils/timeFormat';
import OneTapApplyButton from '../../components/OneTapApplyButton';
import CertificationPromptCard from '../../components/CertificationPromptCard';
import { isFlatPay, formatPay } from '../../utils/payFormat';

/**
 * Iter-187 — Mobile-first conversion-focused gig apply page.
 *
 * Backs `/talent/shifts/:shiftId` and `/talent/gigs/:shiftId` — the
 * canonical deep link in every notification email / SMS / in-app card.
 * The full info-bundle (event title, role, duties, date/time, pay,
 * location, dress code, requirements, client notes) renders BEFORE
 * the Apply CTA so talent can decide quickly. Blockers surface
 * via OneTapApplyButton's structured blocker dialog.
 */
const TalentShiftDetail = () => {
  const { shiftId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const src = searchParams.get('src') || '';

  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [applying, setApplying] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null);
  const [gigSchedule, setGigSchedule] = useState(null);

  useEffect(() => {
    if (!shiftId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/talent/gigs/${shiftId}/schedule`);
        if (!cancelled) setGigSchedule(res.data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [shiftId]);

  useEffect(() => {
    (async () => {
      if (!shiftId) {
        setErrorDetail('This opportunity is no longer available');
        setLoading(false);
        return;
      }
      try {
        const res = await api.get(`/talent/gigs/${shiftId}`);
        setShift(res.data);
        if (src === 'replacement' || res.data?.has_pending_replacement_offer) {
          try {
            const offers = await api.get('/smart-replacement/offers');
            const match = (offers.data || []).find(
              (o) => String(o.shift_id) === String(shiftId),
            );
            if (match) setPendingOffer(match);
          } catch {
            // Non-fatal — apply still works.
          }
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || 'This opportunity is no longer available';
        setErrorDetail(detail);
      } finally {
        setLoading(false);
      }
    })();
  }, [shiftId, src]);

  const acceptReplacement = async () => {
    if (!pendingOffer) {
      // Stale cache fallback — try a regular apply.
      setApplying(true);
      try {
        await talentAPI.applyToShift({ shift_id: shift.id, payout_terms_acknowledged: true });
        toast.success('Application submitted');
        setShift({ ...shift, already_applied: true });
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Failed to apply');
      } finally {
        setApplying(false);
      }
      return;
    }
    setApplying(true);
    try {
      await api.post(`/smart-replacement/offers/${pendingOffer.candidate_id}/accept`);
      toast.success("You're booked! See it under Upcoming Shifts.");
      navigate('/talent/bookings');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to accept offer');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        data-testid="shift-detail-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (errorDetail || !shift) {
    return (
      <div className="min-h-screen bg-gray-50 py-10" data-testid="shift-detail-unavailable">
        <div className="max-w-md mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/talent/shifts')}
            className="mb-4 text-gray-600"
            data-testid="shift-detail-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Browse other gigs
          </Button>
          <Card className="bg-white border border-amber-200 shadow-sm">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                This opportunity is no longer available
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {errorDetail && errorDetail !== 'This opportunity is no longer available'
                  ? errorDetail
                  : 'It may have been filled, cancelled, or released to another city.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  onClick={() => navigate('/talent/shifts')}
                  className="bg-cyan-500 hover:bg-cyan-600"
                  data-testid="shift-detail-browse-gigs-btn"
                >
                  Browse open gigs
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/talent/home')}
                  data-testid="shift-detail-home-btn"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isUrgent = src === 'replacement' || shift.has_pending_replacement_offer;
  const isBackupOnly = !!shift.is_backup_only;
  const isStandbyOnly = !!shift.is_standby_only;
  const isFull = !!shift.is_full;
  // Iter-196 — defensive number / string coercion so a single null /
  // string field from the backend can never white-screen this page.
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const hourlyRate = num(shift.hourly_rate);
  const standbyPay = num(shift.standby_pay_amount);
  const standbyHourly = num(shift.standby_hourly_rate);
  const eventTitle = ((shift.event_title || `${shift.role_name || 'Open'} Gig`) + '').trim();
  const roleName = shift.role_name || 'Open Gig';
  const dateLabel = (shift.is_date_tbd || !shift.gig_date) ? 'Date: TBD' : formatLongDate(shift.gig_date);
  const tzLabel = tzAbbrev(shift.timezone, shift.state, shift.gig_date);
  const timeLabel =
    shift.is_time_tbd
      ? 'Time: TBD'
      : (shift.start_time && shift.end_time
          ? `${formatTimeRange(shift.start_time, shift.end_time)}${tzLabel ? ` ${tzLabel}` : ''}`
          : (formatTime12h(shift.start_time) || 'Time: TBD'));
  const certs = Array.isArray(shift.required_certifications) ? shift.required_certifications : [];
  const dressCodeChecklist = Array.isArray(shift.dress_code_checklist) ? shift.dress_code_checklist : [];
  const attire = Array.isArray(shift.attire_restrictions) ? shift.attire_restrictions : [];
  const grooming = Array.isArray(shift.grooming_requirements) ? shift.grooming_requirements : [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shift-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-600"
            data-testid="shift-detail-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-1.5">
            {isBackupOnly && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-200" data-testid="backup-opportunity-badge">
                Backup Opportunity
              </Badge>
            )}
            {isStandbyOnly && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid="standby-opportunity-badge">
                Onsite Standby
              </Badge>
            )}
            {isUrgent && (
              <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="urgent-badge">
                Urgent Fill
              </Badge>
            )}
            {shift.is_featured && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="featured-badge">
                <Sparkles className="w-3 h-3 mr-0.5" /> Featured
              </Badge>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Hero — event title + role + venue */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-5 sm:p-6">
            <h1
              className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight"
              data-testid="shift-event-title"
            >
              {eventTitle}
            </h1>
            <p className="text-sm sm:text-base text-cyan-700 font-medium mt-1.5" data-testid="shift-role-name">
              {roleName}
            </p>
            {shift.venue_name && (
              <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5" data-testid="shift-venue">
                <Building2 className="w-3.5 h-3.5" /> {shift.venue_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Quick facts grid — date / time / pay / location / spots */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="space-y-0.5" data-testid="fact-date">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Date
                </p>
                <p className="font-semibold text-gray-900">{dateLabel}</p>
              </div>
              <div className="space-y-0.5" data-testid="fact-time">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Time
                </p>
                <p className="font-semibold text-gray-900">{timeLabel}</p>
              </div>
              {(hourlyRate != null || isFlatPay(shift)) && (
                <div className="space-y-0.5" data-testid="fact-pay">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Pay
                  </p>
                  <p className="font-semibold text-emerald-700">{formatPay(shift, hourlyRate)}</p>
                  {isFlatPay(shift) && (
                    <p className="text-[11px] text-emerald-600">Fixed total — paid regardless of hours</p>
                  )}
                </div>
              )}
              <div className="space-y-0.5" data-testid="fact-location">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Location
                </p>
                <p className="font-semibold text-gray-900">
                  {shift.city}{shift.state ? `, ${shift.state}` : ''}
                </p>
              </div>
            </div>
            {shift.spots_remaining != null && shift.spots_remaining > 0 && !isBackupOnly && !isStandbyOnly && (
              <p className="text-xs text-amber-700 mt-3 flex items-center gap-1" data-testid="fact-spots">
                <Users className="w-3.5 h-3.5" />
                {shift.spots_remaining} spot{shift.spots_remaining === 1 ? '' : 's'} remaining
              </p>
            )}
            {isBackupOnly && (
              <p className="text-xs text-purple-700 mt-3 flex items-center gap-1" data-testid="fact-backup-spots">
                <Users className="w-3.5 h-3.5" />
                Primary roster is full · {shift.backup_spots_remaining} backup spot{shift.backup_spots_remaining === 1 ? '' : 's'} open
              </p>
            )}
            {isStandbyOnly && (
              <p className="text-xs text-amber-700 mt-3 flex items-center gap-1" data-testid="fact-standby-spots">
                <Users className="w-3.5 h-3.5" />
                Onsite standby roster open · {shift.standby_unlimited ? 'unlimited spots' : `${shift.standby_slots_remaining} spot${shift.standby_slots_remaining === 1 ? '' : 's'}`}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Duties */}
        {shift.duties && (
          <Card className="bg-white border border-gray-200 shadow-sm" data-testid="duties-card">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <ListChecks className="w-4 h-4 text-cyan-600" /> What you'll do
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{shift.duties}</p>
            </CardContent>
          </Card>
        )}

        {/* Dress code */}
        {(shift.dress_code_name || shift.dress_code_description || dressCodeChecklist.length > 0) && (
          <Card className="bg-white border border-gray-200 shadow-sm" data-testid="dress-code-card">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <ShirtIcon className="w-4 h-4 text-violet-600" /> Dress code
                {shift.dress_code_name && (
                  <Badge variant="outline" className="ml-1 text-[10px] border-violet-200 bg-violet-50 text-violet-700">
                    {shift.dress_code_name}
                  </Badge>
                )}
              </h3>
              {shift.dress_code_description && (
                <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
                  {shift.dress_code_description}
                </p>
              )}
              {dressCodeChecklist.length > 0 && (
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  {dressCodeChecklist.map((item, i) => (
                    <li key={i}>{typeof item === 'string' ? item : (item?.label || item?.text || '')}</li>
                  ))}
                </ul>
              )}
              {(attire.length > 0 || grooming.length > 0) && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                  {attire.length > 0 && (
                    <div>
                      <p className="font-medium text-gray-700">Attire restrictions</p>
                      <ul className="list-disc list-inside">
                        {attire.map((a, i) => (
                          <li key={i}>{typeof a === 'string' ? a : (a?.label || '')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {grooming.length > 0 && (
                    <div>
                      <p className="font-medium text-gray-700">Grooming</p>
                      <ul className="list-disc list-inside">
                        {grooming.map((g, i) => (
                          <li key={i}>{typeof g === 'string' ? g : (g?.label || '')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Requirements / certifications */}
        {(shift.certification_required || certs.length > 0) && (
          <Card className="bg-white border border-gray-200 shadow-sm" data-testid="requirements-card">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4 text-emerald-600" /> Requirements
              </h3>
              {certs.length > 0 ? (
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  {certs.map((c, i) => (
                    <li key={i}>
                      {typeof c === 'string' ? c : (c?.name || c?.label || 'Certification')}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-700">Certification required to apply.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Client notes */}
        {(shift.additional_notes || shift.important_notes) && (
          <Card className="bg-amber-50 border border-amber-200 shadow-sm" data-testid="notes-card">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-semibold text-amber-900 mb-1.5">Client notes</h3>
              {shift.important_notes && (
                <p className="text-sm text-amber-900 whitespace-pre-wrap mb-2">{shift.important_notes}</p>
              )}
              {shift.additional_notes && (
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{shift.additional_notes}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* 2026-06 FIX — Logistics, onsite contact & reporting. Surfaced so
            booked talent see every instruction the admin/client entered. */}
        {(shift.arrival_instructions || shift.entrance_instructions || shift.onsite_directions ||
          shift.parking_instructions || shift.setup_instructions || shift.reporting_location ||
          shift.contact_instructions || shift.recap_required || shift.recap_photo_min ||
          (Array.isArray(shift.recap_required_shots) && shift.recap_required_shots.length > 0)) && (
          <Card className="bg-white border border-gray-200 shadow-sm" data-testid="logistics-card">
            <CardContent className="p-4 sm:p-5 space-y-2 text-sm text-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1.5">
                <ListChecks className="w-4 h-4 text-cyan-600" /> Logistics & reporting
              </h3>
              {shift.reporting_location && <div><span className="font-semibold">Reporting location: </span>{shift.reporting_location}</div>}
              {shift.arrival_instructions && <div><span className="font-semibold">Arrival: </span>{shift.arrival_instructions}</div>}
              {shift.entrance_instructions && <div><span className="font-semibold">Entrance: </span>{shift.entrance_instructions}</div>}
              {shift.onsite_directions && <div><span className="font-semibold">Onsite: </span>{shift.onsite_directions}</div>}
              {shift.parking_instructions && <div><span className="font-semibold">Parking: </span>{shift.parking_instructions}</div>}
              {shift.setup_instructions && <div><span className="font-semibold">Setup: </span>{shift.setup_instructions}</div>}
              {shift.contact_instructions && (
                <div className="pt-1 border-t border-gray-100"><span className="font-semibold">Onsite contact: </span><span className="whitespace-pre-wrap">{shift.contact_instructions}</span></div>
              )}
              {(shift.recap_required || shift.recap_photo_min || (Array.isArray(shift.recap_required_shots) && shift.recap_required_shots.length > 0)) && (
                <div className="pt-1 border-t border-gray-100">
                  <span className="font-semibold">Reporting requirements: </span>
                  {shift.recap_photo_min ? `at least ${shift.recap_photo_min} photo(s); ` : ''}
                  {Array.isArray(shift.recap_required_shots) && shift.recap_required_shots.length > 0
                    ? shift.recap_required_shots.map((s) => (typeof s === 'string' ? s : (s?.label || s?.name || ''))).filter(Boolean).join(', ')
                    : (shift.recap_required ? 'post-event recap required' : '')}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Iter-190 — Onsite Standby info card. Surfaced only when this
            is presented as a standby-only opportunity so talent know
            exactly that standby is on-site, what compensation looks
            like, and how activation works before joining the list. */}
        {isStandbyOnly && (
          <Card className="bg-amber-50 border border-amber-300 shadow-sm" data-testid="standby-info-card">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <Users className="w-4 h-4" /> About this Onsite Standby Opportunity
              </h3>
              <p className="text-sm text-amber-900">
                You are being added as <strong>onsite standby coverage</strong> for this event.
                Standby talent must remain onsite and ready to step in immediately if a
                primary talent no-shows, cancels, or leaves early.
              </p>
              <p className="text-xs text-amber-800">
                Standby talent may not actively work the shift unless activated by the
                onsite manager. We'll notify you instantly via push, SMS, and email if your
                standby is activated.
              </p>
              {shift.standby_description && (
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{shift.standby_description}</p>
              )}
              {(standbyPay || standbyHourly) && (
                <div className="text-sm text-amber-900 space-y-1 border-t border-amber-200 pt-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Compensation</p>
                  {standbyPay > 0 && (
                    <p>
                      You'll receive <strong>${standbyPay.toFixed(2)}</strong> in
                      onsite standby compensation for staying available at the venue.
                    </p>
                  )}
                  {standbyHourly > 0 && (
                    <p>
                      If activated into the shift, your <strong>${standbyHourly.toFixed(2)}/hr</strong>{' '}
                      rate begins the moment activation occurs.
                    </p>
                  )}
                </div>
              )}
              {shift.standby_notes && (
                <p className="text-xs text-amber-800 italic">{shift.standby_notes}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* 2026-08-31 — Full assignment schedule: every shift of a
            multi-day assignment with totals derived from those rows. */}
        {gigSchedule?.is_multi_day && (
          <Card className="border border-slate-200" data-testid="full-assignment-schedule">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Full Assignment Schedule ({gigSchedule.shifts.length} shifts)
              </h3>
              <div className="space-y-1.5">
                {gigSchedule.shifts.map((s, i) => (
                  <div key={`${s.shift_id}-${s.date}`} className="flex items-center justify-between text-sm" data-testid={`schedule-shift-${i + 1}`}>
                    <span className="text-gray-700">
                      <span className="font-medium">Shift {i + 1}:</span>{' '}
                      {formatEventWhen(s.date, s.start_time, s.end_time, shift.timezone, shift.state)}
                    </span>
                    <span className="font-medium text-gray-900">{s.hours} hrs</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm pt-2 mt-1 border-t border-slate-200">
                  <span className="font-semibold text-gray-900">Total scheduled hours</span>
                  <span className="font-bold text-cyan-700" data-testid="schedule-total-hours">{gigSchedule.total_hours} hrs</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Estimated total earnings (net of OA insurance)</span>
                  <span className="font-bold text-emerald-600" data-testid="schedule-estimated-total">${gigSchedule.estimated_total_earnings.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* V424 — Payout terms disclosure: visible before applying AND after booking */}
        {shift.payout_terms && (
          <Card className="bg-emerald-50 border border-emerald-200" data-testid="payout-terms-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-emerald-900">Payout Terms</h3>
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="payout-terms-badge">
                      {shift.payout_terms.label}
                    </Badge>
                    {shift.payout_terms.expedited && (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="critical-pay-badge">
                        <Sparkles className="w-3 h-3 mr-1" /> {shift.payout_terms.expedited_label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-emerald-800 mt-1" data-testid="payout-terms-text">
                    {shift.payout_terms.disclaimer}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Iter-225d / Phase 1C-A — Non-blocking role-cert prompt above the CTA. */}
        <CertificationPromptCard shiftId={shift.id} />

        {/* Apply CTA — sticky on mobile so it never scrolls off-screen */}
        <div
          className="sticky bottom-3 z-20 mt-2"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <Card className="bg-white border border-gray-200 shadow-xl">
            <CardContent className="p-4 flex flex-col sm:flex-row gap-2 items-stretch">
              {shift.is_booked ? (
                <Button
                  onClick={() => navigate('/talent/bookings')}
                  className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-11"
                  data-testid="view-booking-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> View booking
                </Button>
              ) : isFull && !shift.already_applied ? (
                <Button
                  disabled
                  className="flex-1 h-11 bg-gray-200 text-gray-600 cursor-not-allowed"
                  data-testid="gig-full-btn"
                >
                  This gig is currently full
                </Button>
              ) : isUrgent && pendingOffer ? (
                <Button
                  onClick={acceptReplacement}
                  disabled={applying}
                  className="bg-red-600 hover:bg-red-700 flex-1 h-11"
                  data-testid="accept-replacement-btn"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Accept urgent fill
                </Button>
              ) : (
                <div className="flex-1" data-testid="apply-cta-wrap">
                  <OneTapApplyButton
                    shiftId={shift.id}
                    backupOnly={isBackupOnly}
                    standbyOnly={isStandbyOnly}
                    onApplied={() => setShift({ ...shift, already_applied: true })}
                    onWithdrawn={() => setShift((s) => ({ ...s, already_applied: false, application_id: null, application_status: null }))}
                  />
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/talent/shifts')}
                className="h-11 sm:flex-none"
                data-testid="browse-more-btn"
              >
                Browse more gigs
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TalentShiftDetail;
